import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { workspaceScopedBase } from '@/lib/workspaceScopedRoutes'

/**
 * Leave the current route if its PATH names workspace-scoped state.
 *
 * Shared by BOTH tenant-scope changes — `useSwitchWorkspace` and
 * `useSwitchOrg` — because both leave behind a URL that asserts a project or
 * application the user can no longer be in.
 *
 * Splitting this out is not tidiness. The first version of the fix put the
 * navigation inside `useSwitchWorkspace` only, and the org path kept the stale
 * deep link: `useSwitchOrg` purged the caches and the localStorage candidates
 * but not the address bar, so the restore waterfall ("URL deep-link wins")
 * reseeded the previous tenant's project — across an ORG boundary, which is the
 * wider of the two. Caught by the prod spec, not by any local check.
 *
 * `replace` so the switch leaves no Back entry returning to a project the
 * current tenant does not contain.
 */
export function useLeaveWorkspaceScopedRoute(): () => void {
  const navigate = useNavigate()
  const location = useLocation()

  return useCallback(() => {
    const base = workspaceScopedBase(location.pathname)
    if (base) navigate(base, { replace: true })
  }, [navigate, location.pathname])
}
